/**
 * Where the amounts actually live.
 *
 * Every figure the customer sees - "pay ₹950, get ₹1,000", "give ₹100, get
 * ₹100" - is a row edited on this screen. Nothing in the app or the API carries
 * a fallback amount, so this is the ONLY place those numbers exist, which is
 * what makes §18's "no hardcoded campaign amounts" true rather than aspirational.
 *
 * Two rules the UI enforces because the consequences are commercial:
 *   An offer's credit can never be less than its pay amount (that would charge a
 *   customer more than they receive).
 *   Only one referral campaign runs at a time - activating one deactivates the
 *   incumbent, server-side, in a transaction.
 */
import { useCallback, useEffect, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import {
  Card,
  CardTitle,
  Chip,
  Field,
  Modal,
  PillButton,
  SelectInput,
  TableHead,
  Td,
  TextInput,
} from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { emitToast } from '../../lib/toastBus'
import { userHasPermission } from '../../lib/permissions'
import { useAuthStore } from '../../store/authStore'
import {
  createReferralCampaign,
  createTopupOffer,
  formatINR,
  listReferralCampaigns,
  listTopupOffers,
  updateReferralCampaign,
  updateTopupOffer,
} from '../../services/walletApi'
import type { ReferralCampaignRecord, TopupOfferRecord } from '../../services/walletApi'

const toLocalDate = (iso: string | null) => (iso ? iso.slice(0, 10) : '')

function OfferModal({
  offer,
  onClose,
  onSaved,
}: {
  offer: TopupOfferRecord | null
  onClose: () => void
  onSaved: () => void
}) {
  const [code, setCode] = useState(offer?.code ?? '')
  const [name, setName] = useState(offer?.name ?? '')
  const [description, setDescription] = useState(offer?.description ?? '')
  const [payAmount, setPayAmount] = useState(offer ? String(Number(offer.pay_amount)) : '')
  const [creditAmount, setCreditAmount] = useState(offer ? String(Number(offer.credit_amount)) : '')
  const [maxPerUser, setMaxPerUser] = useState(offer?.max_per_user != null ? String(offer.max_per_user) : '')
  const [maxRedemptions, setMaxRedemptions] = useState(offer?.max_redemptions != null ? String(offer.max_redemptions) : '')
  const [endsAt, setEndsAt] = useState(toLocalDate(offer?.ends_at ?? null))
  const [saving, setSaving] = useState(false)

  const pay = Number(payAmount)
  const credit = Number(creditAmount)
  const amountsValid = Number.isFinite(pay) && pay > 0 && Number.isFinite(credit) && credit >= pay
  const canSave = Boolean(code.trim() && name.trim()) && amountsValid && !saving
  const bonus = amountsValid ? credit - pay : 0

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description.trim() || null,
        pay_amount: pay,
        credit_amount: credit,
        max_per_user: maxPerUser ? Number(maxPerUser) : null,
        max_redemptions: maxRedemptions ? Number(maxRedemptions) : null,
        ends_at: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null,
      }
      if (offer) await updateTopupOffer(offer.id, payload as never)
      else await createTopupOffer(payload as never)
      emitToast(offer ? 'Offer updated' : 'Offer created', 'success')
      onSaved()
      onClose()
    } catch {
      // The shared axios instance toasts the server's message.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={offer ? `Edit ${offer.code}` : 'New top-up offer'}
      onClose={onClose}
      width="480px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</PillButton>
          <PillButton onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Save offer'}</PillButton>
        </>
      }
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <Field label="Code" hint="Shown in reports and on the ledger row.">
          <TextInput
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="TOPUP1000"
            disabled={Boolean(offer)}
          />
        </Field>
        <Field label="Name" hint="What the customer reads in the app.">
          <TextInput value={name} onChange={(event) => setName(event.target.value)} placeholder="Add ₹1,000, pay ₹950" />
        </Field>
        <Field label="Description">
          <TextInput value={description ?? ''} onChange={(event) => setDescription(event.target.value)} placeholder="Save ₹50" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="Customer pays (₹)">
            <TextInput type="number" min="1" value={payAmount} onChange={(event) => setPayAmount(event.target.value)} />
          </Field>
          <Field
            label="Wallet receives (₹)"
            error={payAmount && creditAmount && !amountsValid ? 'Must be at least what the customer pays.' : undefined}
          >
            <TextInput
              type="number"
              min="1"
              value={creditAmount}
              invalid={Boolean(payAmount && creditAmount && !amountsValid)}
              onChange={(event) => setCreditAmount(event.target.value)}
            />
          </Field>
        </div>

        {amountsValid ? (
          <Chip tone="gold">Promotional bonus: {formatINR(bonus)}</Chip>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="Max per customer" hint="Blank = unlimited">
            <TextInput type="number" min="1" value={maxPerUser} onChange={(event) => setMaxPerUser(event.target.value)} />
          </Field>
          <Field label="Max total redemptions" hint="Blank = unlimited">
            <TextInput type="number" min="1" value={maxRedemptions} onChange={(event) => setMaxRedemptions(event.target.value)} />
          </Field>
        </div>

        <Field label="Ends on" hint="Blank = runs until switched off. Expired offers stop being purchasable automatically.">
          <TextInput type="date" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

function CampaignModal({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: ReferralCampaignRecord | null
  onClose: () => void
  onSaved: () => void
}) {
  const colors = useColors()
  const [name, setName] = useState(campaign?.name ?? '')
  const [referrerReward, setReferrerReward] = useState(campaign ? String(Number(campaign.referrer_reward_amount)) : '100')
  const [refereeReward, setRefereeReward] = useState(campaign ? String(Number(campaign.referee_reward_amount)) : '100')
  const [rewardKind, setRewardKind] = useState(campaign?.reward_kind ?? 'promo_credit')
  const [qualifyingEvent, setQualifyingEvent] = useState(campaign?.qualifying_event ?? 'FIRST_COMPLETED_ORDER')
  const [refereeTiming, setRefereeTiming] = useState(campaign?.referee_reward_timing ?? 'qualification')
  const [minOrder, setMinOrder] = useState(campaign ? String(Number(campaign.min_order_amount)) : '0')
  const [maxPerReferrer, setMaxPerReferrer] = useState(
    campaign?.max_rewards_per_referrer != null ? String(campaign.max_rewards_per_referrer) : '',
  )
  const [endsAt, setEndsAt] = useState(toLocalDate(campaign?.ends_at ?? null))
  const [saving, setSaving] = useState(false)

  const canSave = Boolean(name.trim()) && !saving

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        referrer_reward_amount: Number(referrerReward) || 0,
        referee_reward_amount: Number(refereeReward) || 0,
        reward_kind: rewardKind,
        qualifying_event: qualifyingEvent,
        referee_reward_timing: refereeTiming,
        min_order_amount: Number(minOrder) || 0,
        max_rewards_per_referrer: maxPerReferrer ? Number(maxPerReferrer) : null,
        ends_at: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null,
        is_active: true,
      }
      if (campaign) await updateReferralCampaign(campaign.id, payload as never)
      else await createReferralCampaign(payload as never)
      emitToast(campaign ? 'Campaign updated' : 'Campaign created and activated', 'success')
      onSaved()
      onClose()
    } catch {
      // Toasted by the shared client.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={campaign ? `Edit ${campaign.name}` : 'New referral campaign'}
      onClose={onClose}
      width="500px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</PillButton>
          <PillButton onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Save campaign'}</PillButton>
        </>
      }
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <Field label="Campaign name">
          <TextInput value={name} onChange={(event) => setName(event.target.value)} placeholder="Give ₹100, Get ₹100" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="Referrer gets (₹)">
            <TextInput type="number" min="0" value={referrerReward} onChange={(event) => setReferrerReward(event.target.value)} />
          </Field>
          <Field label="Friend gets (₹)">
            <TextInput type="number" min="0" value={refereeReward} onChange={(event) => setRefereeReward(event.target.value)} />
          </Field>
        </div>

        <Field
          label="Reward type"
          hint="Promotional credit keeps giveaways out of the refundable liability. Wallet credit treats them as money we owe."
        >
          <SelectInput value={rewardKind} onChange={(event) => setRewardKind(event.target.value as never)}>
            <option value="promo_credit">Promotional credit (recommended)</option>
            <option value="wallet_credit">Wallet credit (refundable)</option>
          </SelectInput>
        </Field>

        <Field
          label="Qualifying event"
          hint="When the reward becomes payable. Never on install or signup."
        >
          <SelectInput value={qualifyingEvent} onChange={(event) => setQualifyingEvent(event.target.value as never)}>
            <option value="FIRST_COMPLETED_ORDER">Friend's first order is delivered</option>
            <option value="FIRST_PAID_ORDER">Friend's first order is paid</option>
          </SelectInput>
        </Field>

        <Field
          label="When the friend is paid"
          hint="Paying at signup is materially more abusable. A fake account costs an attacker one SIM."
        >
          <SelectInput value={refereeTiming} onChange={(event) => setRefereeTiming(event.target.value as never)}>
            <option value="qualification">At qualification (recommended)</option>
            <option value="signup">At signup</option>
          </SelectInput>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="Minimum order value (₹)">
            <TextInput type="number" min="0" value={minOrder} onChange={(event) => setMinOrder(event.target.value)} />
          </Field>
          <Field label="Max rewards per referrer" hint="Blank = unlimited">
            <TextInput type="number" min="1" value={maxPerReferrer} onChange={(event) => setMaxPerReferrer(event.target.value)} />
          </Field>
        </div>

        <Field label="Ends on" hint="After this date, attributed referrals stop paying out.">
          <TextInput type="date" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} />
        </Field>

        <div style={{ fontSize: '12px', color: colors.textMuted }}>
          Saving activates this campaign. Only one referral campaign runs at a time,
          so the current one is switched off in the same step.
        </div>
      </div>
    </Modal>
  )
}

export default function CampaignsTab() {
  const colors = useColors()
  const user = useAuthStore((state) => state.user)
  const canManage = userHasPermission(user, 'wallet.manage')

  const [offers, setOffers] = useState<TopupOfferRecord[]>([])
  const [campaigns, setCampaigns] = useState<ReferralCampaignRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingOffer, setEditingOffer] = useState<TopupOfferRecord | null>(null)
  const [creatingOffer, setCreatingOffer] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<ReferralCampaignRecord | null>(null)
  const [creatingCampaign, setCreatingCampaign] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [offerList, campaignList] = await Promise.all([listTopupOffers(), listReferralCampaigns()])
      setOffers(offerList)
      setCampaigns(campaignList)
    } catch {
      setError('Could not load offers and campaigns.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const toggleOffer = async (offer: TopupOfferRecord) => {
    try {
      await updateTopupOffer(offer.id, { is_active: !offer.is_active })
      emitToast(offer.is_active ? `${offer.code} switched off` : `${offer.code} switched on`, 'success')
      void load()
    } catch {
      // Toasted by the shared client.
    }
  }

  if (error) return <LoadErrorState message={error} onRetry={load} />

  const activeCampaign = campaigns.find((campaign) => campaign.is_active)

  return (
    <>
      <Card padded={false} style={{ marginBottom: '18px' }}>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap', gap: '10px',
          }}
        >
          <CardTitle>Top-up offers</CardTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            <PillButton variant="secondary" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </PillButton>
            {canManage ? (
              <PillButton onClick={() => setCreatingOffer(true)}>
                <Plus size={14} /> New offer
              </PillButton>
            ) : null}
          </div>
        </div>

        {offers.length === 0 && !loading ? (
          <div style={{ padding: '20px' }}>
            <EmptyState title="No offers" description="Create one so customers can add money at a discount." />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '880px' }}>
              <TableHead
                columns={[
                  { key: 'offer', label: 'Offer' },
                  { key: 'pay', label: 'Customer pays', align: 'right' },
                  { key: 'credit', label: 'Wallet gets', align: 'right' },
                  { key: 'bonus', label: 'Our cost', align: 'right' },
                  { key: 'limits', label: 'Limits' },
                  { key: 'used', label: 'Redeemed', align: 'right' },
                  { key: 'status', label: 'Status' },
                  { key: 'actions', label: '' },
                ]}
              />
              <tbody>
                {offers.map((offer) => {
                  const expired = Boolean(offer.ends_at && new Date(offer.ends_at) <= new Date())
                  return (
                    <tr key={offer.id}>
                      <Td>
                        <div style={{ fontWeight: 600 }}>{offer.name}</div>
                        <div style={{ fontSize: '12px', color: colors.textMuted, fontFamily: 'monospace' }}>
                          {offer.code}
                        </div>
                      </Td>
                      <Td align="right" strong>{formatINR(offer.pay_amount)}</Td>
                      <Td align="right" strong>{formatINR(offer.credit_amount)}</Td>
                      <Td align="right" style={{ color: '#B45309' }}>{formatINR(offer.bonus_amount)}</Td>
                      <Td muted>
                        <div style={{ fontSize: '12px' }}>
                          {offer.max_per_user ? `${offer.max_per_user} per customer` : 'Unlimited per customer'}
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          {offer.max_redemptions ? `${offer.max_redemptions} total` : 'No total cap'}
                        </div>
                        {offer.ends_at ? (
                          <div style={{ fontSize: '12px' }}>
                            Ends {new Date(offer.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </div>
                        ) : null}
                      </Td>
                      <Td align="right">{offer.total_redemptions}</Td>
                      <Td>
                        {expired
                          ? <Chip tone="neutral">Expired</Chip>
                          : offer.is_active
                            ? <Chip tone="success">Live</Chip>
                            : <Chip tone="warning">Off</Chip>}
                      </Td>
                      <Td align="right">
                        {canManage ? (
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <PillButton variant="secondary" onClick={() => setEditingOffer(offer)}>Edit</PillButton>
                            <PillButton variant="secondary" onClick={() => toggleOffer(offer)}>
                              {offer.is_active ? 'Switch off' : 'Switch on'}
                            </PillButton>
                          </div>
                        ) : null}
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap', gap: '10px',
          }}
        >
          <CardTitle
            right={
              activeCampaign
                ? <Chip tone="success">Running: {activeCampaign.name}</Chip>
                : <Chip tone="warning">No campaign running</Chip>
            }
          >
            Referral campaigns
          </CardTitle>
          {canManage ? (
            <PillButton onClick={() => setCreatingCampaign(true)}>
              <Plus size={14} /> New campaign
            </PillButton>
          ) : null}
        </div>

        {campaigns.length === 0 && !loading ? (
          <div style={{ padding: '20px' }}>
            <EmptyState
              title="No campaigns"
              description="Create one to start rewarding referrals. Without an active campaign, codes are accepted but nothing is paid."
            />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '880px' }}>
              <TableHead
                columns={[
                  { key: 'name', label: 'Campaign' },
                  { key: 'referrer', label: 'Referrer gets', align: 'right' },
                  { key: 'referee', label: 'Friend gets', align: 'right' },
                  { key: 'event', label: 'Pays out when' },
                  { key: 'caps', label: 'Caps' },
                  { key: 'status', label: 'Status' },
                  { key: 'actions', label: '' },
                ]}
              />
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <Td strong>{campaign.name}</Td>
                    <Td align="right" strong>{formatINR(campaign.referrer_reward_amount)}</Td>
                    <Td align="right" strong>{formatINR(campaign.referee_reward_amount)}</Td>
                    <Td muted>
                      <div style={{ fontSize: '12px' }}>
                        {campaign.qualifying_event === 'FIRST_COMPLETED_ORDER'
                          ? "Friend's first order is delivered"
                          : "Friend's first order is paid"}
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        Friend paid {campaign.referee_reward_timing === 'signup' ? 'at signup' : 'at qualification'}
                      </div>
                    </Td>
                    <Td muted>
                      <div style={{ fontSize: '12px' }}>
                        {campaign.max_rewards_per_referrer
                          ? `${campaign.max_rewards_per_referrer} per referrer`
                          : 'No referrer cap'}
                      </div>
                      {Number(campaign.min_order_amount) > 0 ? (
                        <div style={{ fontSize: '12px' }}>Min order {formatINR(campaign.min_order_amount)}</div>
                      ) : null}
                      {campaign.ends_at ? (
                        <div style={{ fontSize: '12px' }}>
                          Ends {new Date(campaign.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                      ) : null}
                    </Td>
                    <Td>
                      {campaign.is_active ? <Chip tone="success">Active</Chip> : <Chip tone="neutral">Ended</Chip>}
                    </Td>
                    <Td align="right">
                      {canManage ? (
                        <PillButton variant="secondary" onClick={() => setEditingCampaign(campaign)}>Edit</PillButton>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creatingOffer || editingOffer ? (
        <OfferModal
          offer={editingOffer}
          onClose={() => {
            setCreatingOffer(false)
            setEditingOffer(null)
          }}
          onSaved={load}
        />
      ) : null}

      {creatingCampaign || editingCampaign ? (
        <CampaignModal
          campaign={editingCampaign}
          onClose={() => {
            setCreatingCampaign(false)
            setEditingCampaign(null)
          }}
          onSaved={load}
        />
      ) : null}
    </>
  )
}
